import { useState, useEffect, useRef } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAccount, useChainId, useReadContract } from 'wagmi';
import BigNumber from 'bignumber.js';
import { useCookies } from 'react-cookie';

import { NETWORK, ADDRESSES, ABIS } from "../config";

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SwitchNetwork } from '../components/SwitchNetwork';
import SessionHandler from '../components/SessionHandler';

import ethosDownload from '../images/EthosDL.png';
import iosDownload from '../images/iOS_Download_Btn.svg';
import androidAPKDownload from '../images/Android_APK_Download_Btn.svg';
import androidDownload from '../images/Android_Download_Btn.svg';

import goldTierTop from '../images/BioFi_gld.png';
import goldTierBottom from '../images/Gold_badge.png';
import platinumTierTop from '../images/BioFi_plt.png';
import platinumTierBottom from '../images/plat_badge.png';
import diamondTierTop from '../images/BioFi_dmd.png';
import diamondTierBottom from '../images/dmd_badge.png';
import emeraldTierTop from '../images/BioFi_emrld.png';
import emeraldTierBottom from '../images/emrld_badge.png';

function addLeadingZero(num) {
  num = num.toString();
  if (num.length < 2) {
    num = "0" + num;
  }
  return num;
}

function convertDate(timestamp) {
  let myDate = new Date(timestamp);
  let dateString = myDate.getFullYear() + "-" + addLeadingZero(myDate.getMonth() + 1) + "-" + addLeadingZero(myDate.getDate());
  return dateString;
}

function convertDecimals(input) {
  return input / 10 ** 18;
}

function convertToDecimals(input) {
  return input * 10 ** 18;
}

function convertBioFiAmount(amount) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
  let convertedString = formatter.format(amount);
  return convertedString.slice(1);
}

export default function BiofiStaking({ validChain }) {
  const [userAccountType, setUserAccountType] = useState('-');
  const [userBiofiBalanceInt, setUserBiofiBalanceInt] = useState(0);
  const [userBiofiBalance, setUserBiofiBalance] = useState('0');
  const [userLockedBiofi, setUserLockedBiofi] = useState('0');
  const [cookies] = useCookies(['userType']);

  const tierData = useRef(null);
  const navigate = useNavigate();

  const { address } = useAccount();
  const chainId = useChainId();
  let isNetworkCorrect = chainId === validChain?.id;

  const { data: userBalanceData, refetch: refetchUserBalance } = useReadContract({
    address: ADDRESSES[NETWORK.id].biofi,
    abi: ABIS.biofi,
    functionName: "balanceOf",
    args: [address],
    query: {
      enabled: !!address,
      refetchInterval: false,
    },
  });

  const { data: userStakeData, refetch: refetchUserStake } = useReadContract({
    address: ADDRESSES[NETWORK.id].staking,
    abi: ABIS.staking,
    functionName: "readStake",
    args: [address],
    query: {
      enabled: !!address,
      refetchInterval: false,
    },
  });

  let isWalletConnected = (address !== null && typeof (address) !== 'undefined');

  useEffect(() => {
    let userType = cookies.userType;
    if (userType === undefined) {
      userType = "user";
    }
    setUserAccountType(userType);
  }, [cookies.userType]);

  function formatInteger(n) {
    let string = String(n);
    if (string.includes('.')) {
      let parts = string.split('.');
      let first = parts[0].replace(/(.)(?=(\d{3})+$)/g, '$1,');
      let result = first + '.' + parts[1];
      return result;
    }
    return String(n).replace(/(.)(?=(\d{3})+$)/g, '$1,');
  }

  async function fetchUserBalance() {
    if (userBalanceData === undefined) return;
    let userBalance = BigNumber(userBalanceData.toString()).dividedBy(10 ** 18);
    let convertedUserBalance = convertBioFiAmount(userBalance);
    setUserBiofiBalance(convertedUserBalance.toString());
    setUserBiofiBalanceInt(Number(userBalance.toFixed()));
  }

  async function fetchUserStake() {
    if (userStakeData === undefined) return;
    let stakeInfo = userStakeData;
    let convertedStakeAmount = 0;
    if (stakeInfo.exists) {
      let stakeAmount = BigNumber(stakeInfo.stakedAmount.toString()).dividedBy(10 ** 18);
      convertedStakeAmount = convertBioFiAmount(stakeAmount);
    }
    setUserLockedBiofi(convertedStakeAmount.toString());
  }

  useEffect(() => {
    fetchUserBalance();
    // eslint-disable-next-line
  }, [userBalanceData]);

  useEffect(() => {
    fetchUserStake();
    // eslint-disable-next-line
  }, [userStakeData]);

  function setupGold() {
    navigate('/deposit/gold');
  }
  function setupPlatinum() {
    navigate('/deposit/platinum');
  }
  function setupDiamond() {
    navigate('/deposit/diamond');
  }
  function setupEmerald() {
    navigate('/deposit/emerald');
  }
  function setupMaple() {
    navigate('/depositLp/maple');
  }
  function setupOak() {
    navigate('/depositLp/oak');
  }

  return (
    <>
      <Navbar validChain={validChain} />
      {
        isNetworkCorrect ? (
          <>
            <div className="bf-stk-dash">
              <div className="stk-dash-cont">
                <div className="bf-stkDashHeader">
                  <h1><span id="userType"> - </span> Rewards Utility: Perks and NFT's</h1>
                </div>
                <div className="bf-dashinfo">
                  <div className="bf-rwdTopControls">
                    <div className="bf-loginInfo"> Logged in user type: <span className="color-teal bold"> {userAccountType} </span> </div>
                    <div className="bf-stkBtnSect"><Link to="/dashboard" id="dashboardButton" className="bf-stk-btn1"> View Dashboard </Link></div>
                  </div>
                  <div className="bf-rwdBtmControls">
                    <div className="bf-biofi-wlt">
                      Balance: <span className="color-teal bf-inputclr2">{userBiofiBalance}</span>
                    </div>
                    <div className="bf-biofi-wlt">
                      Locked BioFi: <span className="color-teal bf-inputclr2">{userLockedBiofi}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <section className="bf-stk-main">
              <h1 style={{ textAlign: "center" }}>$BioFi LP rewards</h1>
              <div className="stk-crd-section">
                <div className="stk-crd-rt">
                  <div className="bf-stk-crd stk-maple" style={{ minHeight: "20rem" }}>
                    <div className="stk-row">
                      <img className="stk-crd-img" src={platinumTierTop} alt="BioFi" />
                    </div>
                    <div className="stk-row stk-title">
                      Maple
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Utility Rewards Rate:<span className="stk-hdrtxt tooltiptext maple-tooltiptext fullUtilityRateStyle">Annual Rewards rate based on meeting utility usage requirement.</span></div>
                      10%
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Usage Period:<span className="stk-hdrtxt tooltiptext usagePeriodStyle maple-tooltiptext"><b>What is utility usage period?</b><br />Required period of time for lockup of LP tokens to earn the Full Utility Rewards Rate.</span></div>
                      90 days
                    </div>
                    <div className="stk-r-setup">
                      <button id="offer3Button" className="bf-btn btn-success setup-button" disabled={false} onClick={setupMaple}>Setup</button>
                    </div>
                  </div>
                  <div className="bf-stk-crd stk-oak" style={{ minHeight: "20rem" }}>
                    <div className="stk-row">
                      <img className="stk-crd-img" src={platinumTierTop} alt="BioFi" />
                    </div>
                    <div className="stk-row stk-title">
                      Oak
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Utility Rewards Rate:<span className="stk-hdrtxt tooltiptext oak-tooltiptext fullUtilityRateStyle">Annual Rewards rate based on meeting utility usage requirement.</span></div>
                      20%
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Usage Period:<span className="stk-hdrtxt tooltiptext usagePeriodStyle oak-tooltiptext"><b>What is utility usage period?</b><br />Required period of time for lockup of LP tokens to earn the Full Utility Rewards Rate.</span></div>
                      120 days
                    </div>
                    <div className="stk-r-setup">
                      <button id="offer4Button" className="bf-btn btn-success setup-button" disabled={false} onClick={setupOak}>Setup</button>
                    </div>
                  </div>
                </div>
              </div>
              <h1 style={{ textAlign: "center" }}>$BioFi rewards</h1>
              <div className="stk-crd-section">
                {/*Responsive Left */}
                <div className="stk-crd-lft">
                  {/*GOLD CARD*/}
                  <div className="bf-stk-crd stk-gld">
                    <div className="stk-row">
                      <img src={goldTierTop} alt="BioFi" className="stk-crd-img" />
                    </div>
                    <div className="stk-row">
                      <img src={goldTierBottom} alt="Gold" className="stk-crd-img2" />
                    </div>
                    <div className="stk-row">
                      <span className="stk-hdrtxt">$BioFi Required: </span>
                      10,000 - 300,000
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Full Utility Rewards Rate:<span className="stk-hdrtxt tooltiptext gold-tooltiptext fullUtilityRateStyle">Annual Rewards rate based on meeting utility usage requirement.</span></div>
                      7%
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Partial Utility Rewards Rate:<span className="stk-hdrtxt tooltiptext gold-tooltiptext">Annual Rewards rate based on not meeting Utility Usage requirement.</span></div>
                      5%
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Utility Usage Requirement:<span className="stk-hdrtxt tooltiptext gold-tooltiptext utilityUsageStyle"><b>What is utility usage?</b><br />Required number of visits to the ecosystem during utility usage period to earn the Full Utility Rewards Rate.</span></div>
                      6 times
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Usage Period:<span className="stk-hdrtxt tooltiptext usagePeriodStyle gold-tooltiptext"><b>What is utility usage period?</b><br />Required period of time for lockup of $BioFi to earn the Full Utility Rewards Rate.</span></div>
                      90 days
                    </div>
                    <div className="stk-row">
                      <div><span className="stk-hdrtxt">Perks:</span></div>
                      Free PhenixLoc: 10 days <br />
                      Access Ethos Metaverse: DogeRun
                    </div>
                    <div className="stk-r-setup">
                      <button id="offer1Button" className="bf-btn btn-success setup-button" disabled={userBiofiBalanceInt < 10000} onClick={setupGold}>Setup</button>
                    </div>
                  </div>
                  <div className="bf-stk-crd stk-plt">
                    <div className="stk-row">
                      <img src={platinumTierTop} alt="BioFi" className="stk-crd-img" />
                    </div>
                    <div className="stk-row">
                      <img className="stk-crd-img2" src={platinumTierBottom} alt="Platinum" />
                    </div>
                    <div className="stk-row">
                      <span className="stk-hdrtxt">$BioFi Required: </span>
                      250,000 - 3,000,000
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Full Utility Rewards Rate:<span className="stk-hdrtxt tooltiptext plat-tooltiptext fullUtilityRateStyle">Annual Rewards rate based on meeting utility usage requirement.</span></div>
                      10%
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Partial Utility Rewards Rate:<span className="stk-hdrtxt tooltiptext plat-tooltiptext">Annual Rewards rate based on not meeting Utility Usage requirement.</span></div>
                      8%
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Utility Usage Requirement:<span className="stk-hdrtxt tooltiptext plat-tooltiptext utilityUsageStyle"><b>What is utility usage?</b><br />Required number of visits to the ecosystem during utility usage period to earn the Full Utility Rewards Rate.</span></div>
                      8 times
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Usage Period:<span className="stk-hdrtxt tooltiptext usagePeriodStyle plat-tooltiptext"><b>What is utility usage period?</b><br />Required period of time for lockup of $BioFi to earn the Full Utility Rewards Rate.</span></div>
                      120 days
                    </div>
                    <div className="stk-row">
                      <div><span className="stk-hdrtxt">Perks:</span></div>
                      Free PhenixLoc: 10 days <br />
                      Access Ethos Metaverse: DogeRun
                    </div>
                    <div className="stk-r-setup">
                      <button id="offer2Button" className="bf-btn btn-success setup-button" disabled={userBiofiBalanceInt < 250000} onClick={setupPlatinum}>Setup</button>
                    </div>
                  </div>
                </div>
                <div className="stk-crd-rt">
                  <div className="bf-stk-crd stk-dmd">
                    <div className="stk-row">
                      <img className="stk-crd-img" src={diamondTierTop} alt="BioFi" />
                    </div>
                    <div className="stk-row">
                      <img className="stk-crd-img2" src={diamondTierBottom} alt="Diamond" />
                    </div>
                    <div className="stk-row">
                      <span className="stk-hdrtxt">$BioFi Required: </span>
                      2,950,000 - 30,000,000
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Full Utility Rewards Rate:<span className="stk-hdrtxt tooltiptext dmd-tooltiptext fullUtilityRateStyle">Annual Rewards rate based on meeting utility usage requirement.</span></div>
                      15%
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Partial Utility Rewards Rate:<span className="stk-hdrtxt tooltiptext dmd-tooltiptext">Annual Rewards rate based on not meeting Utility Usage requirement.</span></div>
                      11%
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Utility Usage Requirement:<span className="stk-hdrtxt tooltiptext dmd-tooltiptext utilityUsageStyle"><b>What is utility usage?</b><br />Required number of visits to the ecosystem during utility usage period to earn the Full Utility Rewards Rate.</span></div>
                      12 times
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Usage Period:<span className="stk-hdrtxt tooltiptext usagePeriodStyle dmd-tooltiptext"><b>What is utility usage period?</b><br />Required period of time for lockup of $BioFi to earn the Full Utility Rewards Rate.</span></div>
                      180 days
                    </div>
                    <div className="stk-row">
                      <div><span className="stk-hdrtxt">Perks:</span></div>
                      <span className="stk-perks-desc">
                        Free PhenixLoc: 10 days <br />
                        Access Ethos Metaverse: DogeRun</span>
                    </div>
                    <div className="stk-r-setup">
                      <button id="offer3Button" className="bf-btn btn-success setup-button" disabled={userBiofiBalanceInt < 2950000} onClick={setupDiamond}>Setup</button>
                    </div>
                  </div>
                  <div className="bf-stk-crd stk-emrld">
                    <div className="stk-row">
                      <img className="stk-crd-img" src={emeraldTierTop} alt="BioFi" />
                    </div>
                    <div className="stk-row">
                      <img className="stk-crd-img2" src={emeraldTierBottom} alt="Emerald" />
                    </div>
                    <div className="stk-row">
                      <span className="stk-hdrtxt">$BioFi Required: </span>
                      29,500,000 - 300,000,000
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Full Utility Rewards Rate:<span className="stk-hdrtxt tooltiptext emrld-tooltiptext fullUtilityRateStyle">Annual Rewards rate based on meeting utility usage requirement.</span></div>
                      20%
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Partial Utility Rewards Rate:<span className="stk-hdrtxt tooltiptext emrld-tooltiptext">Annual Rewards rate based on not meeting Utility Usage requirement.</span></div>
                      16%
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Utility Usage Requirement:<span className="stk-hdrtxt tooltiptext emrld-tooltiptext utilityUsageStyle"><b>What is utility usage?</b><br />Required number of visits to the ecosystem during utility usage period to earn the Full Utility Rewards Rate.</span></div>
                      24 times
                    </div>
                    <div className="stk-row">
                      <div className="customTooltip">Usage Period:<span className="stk-hdrtxt tooltiptext usagePeriodStyle emrld-tooltiptext"><b>What is utility usage period?</b><br />Required period of time for lockup of $BioFi to earn the Full Utility Rewards Rate.</span></div>
                      360 days
                    </div>
                    <div className="stk-row">
                      <div><span className="stk-hdrtxt">Perks:</span></div>
                      <span className="stk-perks-desc"> Free PhenixLoc: 10 days <br />
                        Access Ethos Metaverse: DogeRun,
                        Free access to Say-Tec API's</span>
                    </div>
                    <div className="stk-r-setup">
                      <button id="offer4Button" className="bf-btn btn-success setup-button" disabled={userBiofiBalanceInt < 29500000} onClick={setupEmerald}>Setup</button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="stk-perks-dl">
                <div className="stk-dl1">
                  <h2>Perks:</h2>
                  Ethos Metaverse download:
                  <a href="https://ethosmetaverse.net/Setup/EthosMetaverseV092-Setup.exe"> <img className="stk-dl-btn" src={ethosDownload} alt="Ethos Download" /></a>
                </div>
                <div className="stk-dl1">
                  PhenixLoc Download:
                  <div className="stk-dl-os">
                    <a href="#"><img src={iosDownload} alt="" className="stk-dl-btn" /></a>
                    <a href="#"><img src={androidAPKDownload} alt="Download Android APK" className="stk-dl-btn" /></a>
                  </div>
                </div>
                <div className="stk-dl1">
                  Krptic Wallet Download:
                  <div className="stk-dl-os">
                    <a href="https://apps.apple.com/gb/app/krptic-wallet/id1571851595"><img src={iosDownload} alt="" className="stk-dl-btn" /></a>
                  </div>
                  <div className="stk-dl-os">
                    <a href="https://play.google.com/store/apps/details?id=com.krptic&hl=en&gl=us"><img src={androidDownload} alt="Download Android APK" className="stk-dl-btn" /></a>
                    <a href="https://www.krptic.com/krptic.apk"><img src={androidAPKDownload} alt="Download Android APK" className="stk-dl-btn" /></a>
                  </div>
                </div>
              </div>
            </section>
          </>
        ) : (
          <SwitchNetwork validChain={validChain} />
        )
      }
      <SessionHandler />
      <Footer />
    </>
  );
}